import { createFileRoute } from "@tanstack/react-router";
import HVIHeader from "@/components/HVIHeader";
import HVIFooter from "@/components/HVIFooter";
import AdSlot from "@/components/AdSlot";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import FeedbackForm from "@/components/FeedbackForm";

const FAQS: { q: string; a: string }[] = [
  {
    q: "Is hantavirus airborne between humans?",
    a: "Most hantaviruses (Sin Nombre, Hantaan, Puumala, Seoul and others) do NOT spread person-to-person through the air — transmission happens by inhaling aerosolised rodent urine, droppings or saliva. The one critical exception is Andes orthohantavirus (ANDV): it has documented person-to-person respiratory transmission, which is exactly what makes it so dangerous. Household clusters and healthcare-worker infections have been recorded repeatedly in Argentina and Chile.",
  },
  {
    q: "Why is Andes virus (ANDV) more dangerous than other hantaviruses?",
    a: "ANDV combines a high case-fatality rate (25–40%) with the unique ability to spread directly between people via close-contact droplets and aerosols during the prodromal and cardiopulmonary phases. This allows family clusters, hospital outbreaks and confined-environment transmission (cruise ships, expedition vessels, aircraft) — something no other hantavirus variant can do.",
  },
  {
    q: "How long after exposure do symptoms appear?",
    a: "Incubation typically lasts 1–8 weeks. Early symptoms include fever, severe muscle aches, headache, fatigue and nausea. Within 4–10 days the disease can progress to coughing and shortness of breath (HPS) or acute kidney failure (HFRS). Once the cardiopulmonary phase begins, deterioration is very rapid.",
  },
  {
    q: "Is there a vaccine or specific treatment?",
    a: "There is no widely-licensed vaccine and no specific antiviral therapy. Treatment is supportive: early intensive-care admission, oxygen, mechanical ventilation, ECMO for severe HPS and dialysis for HFRS. Survival improves dramatically with early hospital presentation.",
  },
  {
    q: "Can I sweep up rodent droppings?",
    a: "No. Sweeping or vacuuming aerosolises the virus and significantly increases infection risk. Always use the 'wet method': ventilate for 30 minutes, wear an N95/FFP2 respirator and gloves, saturate with a 1:10 bleach solution, soak for at least 5 minutes, lift with paper towels and double-bag the waste.",
  },
  {
    q: "Can pets transmit hantavirus?",
    a: "Cats and dogs do not carry or transmit hantavirus to humans. However, pet rats are a confirmed source of Seoul virus (SEOV) — household clusters linked to pet rats have been documented in the United States and Europe.",
  },
  {
    q: "What should I do after travelling to an endemic region?",
    a: "Avoid sleeping in cabins, barns or storage spaces with visible rodent droppings, and ventilate enclosed spaces before entering. If you develop fever, muscle aches or shortness of breath within 6 weeks of returning from ANDV regions (Patagonia, Chile, Argentina, Antarctic expeditions), go to an emergency department immediately and disclose your travel history.",
  },
];

import { buildSeo, SITE_URL, PUBLISHER } from "@/lib/seo";
import { useT } from "@/lib/i18n";
import { useDomTranslate } from "@/hooks/useDomTranslate";

export const Route = createFileRoute("/resources")({
  component: ResourcesPage,
  head: () =>
    buildSeo({
      path: "/resources",
      title: "Hantavirus Prevention Guide & Variant Reference",
      description:
        "Evidence-based hantavirus prevention protocols, clinical guidelines and a complete variant reference covering Andes, Sin Nombre, Hantaan, Seoul, Puumala and Dobrava orthohantaviruses.",
      image: "/og/resources.jpg",
      keywords: [
        "hantavirus prevention",
        "hantavirus symptoms",
        "rodent cleanup protocol",
        "N95 wet method",
        "andes virus variant",
        "puumala hantaan seoul",
        "HPS clinical guidelines",
      ],
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "Hantavirus Prevention Guide & Variant Reference",
          url: `${SITE_URL}/resources`,
          about: {
            "@type": "MedicalCondition",
            name: "Hantavirus infection",
            alternateName: ["HPS", "HCPS", "HFRS"],
            code: { "@type": "MedicalCode", codingSystem: "ICD-10", code: "B33.4" },
          },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Clinicians, public health, travellers",
          },
          lastReviewed: new Date().toISOString().slice(0, 10),
          publisher: PUBLISHER,
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    }),
});

const VARIANTS: {
  name: string;
  abbreviation: string;
  disease: string;
  region: string;
  reservoir: string;
  mortality: string;
  active: boolean;
}[] = [
  { name: "Andes orthohantavirus", abbreviation: "ANDV", disease: "HPS/HCPS", region: "South America (Argentina, Chile)", reservoir: "Oligoryzomys longicaudatus (Long-tailed pygmy rice rat)", mortality: "25–40%", active: true },
  { name: "Sin Nombre orthohantavirus", abbreviation: "SNV", disease: "HPS", region: "North America (USA, Canada)", reservoir: "Peromyscus maniculatus (Deer mouse)", mortality: "35–40%", active: false },
  { name: "Hantaan orthohantavirus", abbreviation: "HTNV", disease: "HFRS", region: "East Asia (China, Korea, Russia)", reservoir: "Apodemus agrarius (Striped field mouse)", mortality: "5–15%", active: false },
  { name: "Seoul orthohantavirus", abbreviation: "SEOV", disease: "HFRS (mild)", region: "Worldwide (urban)", reservoir: "Rattus norvegicus (Brown rat)", mortality: "1–2%", active: false },
  { name: "Puumala orthohantavirus", abbreviation: "PUUV", disease: "NE (Nephropathia epidemica)", region: "Europe (Scandinavia, Central Europe)", reservoir: "Myodes glareolus (Bank vole)", mortality: "<1%", active: false },
  { name: "Dobrava-Belgrade orthohantavirus", abbreviation: "DOBV", disease: "HFRS", region: "Balkans, Southeast Europe", reservoir: "Apodemus flavicollis (Yellow-necked mouse)", mortality: "5–12%", active: false },
  { name: "Laguna Negra orthohantavirus", abbreviation: "LANV", disease: "HPS", region: "South America (Paraguay, Bolivia)", reservoir: "Calomys laucha (Small vesper mouse)", mortality: "10–20%", active: false },
  { name: "Choclo orthohantavirus", abbreviation: "CHOV", disease: "HPS", region: "Central America (Panama)", reservoir: "Oligoryzomys fulvescens (Fulvous pygmy rice rat)", mortality: "10–25%", active: false },
  { name: "Black Creek Canal orthohantavirus", abbreviation: "BCCV", disease: "HPS", region: "North America (Florida, USA)", reservoir: "Sigmodon hispidus (Hispid cotton rat)", mortality: "~40%", active: false },
  { name: "Tula orthohantavirus", abbreviation: "TULV", disease: "Mild/Asymptomatic", region: "Europe", reservoir: "Microtus arvalis (Common vole)", mortality: "Rare/Low", active: false },
];

function ResourcesPage() {
  const { lang } = useT();
  useDomTranslate("#resources-content", lang, lang);
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground font-sans">
      <HVIHeader />

      <main id="resources-content" key={lang} className="flex-1 overflow-y-auto">
        <section id="faq" className="px-4 md:px-6 pt-6 md:pt-8 pb-6 border-b border-border scroll-mt-20">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-hvi-primary text-lg">❓</span>
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Frequently Asked Questions</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Quick answers to the most common public-health questions about hantavirus, ANDV airborne transmission, symptoms, treatment and prevention.
          </p>
          <div className="rounded-2xl border border-border bg-card px-4 md:px-5">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-foreground">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-hvi-on-surface-variant leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
        <div className="px-4 md:px-6 py-6 md:py-8 border-b border-border">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground">Guide &amp; Prevention</h1>
          <p className="text-sm text-muted-foreground mt-2">Prevention protocols, clinical guidelines, and hantavirus variant reference.</p>
          <div className="mt-4 border border-[#eab308]/50 bg-[#eab308]/10 px-4 py-3 flex items-start gap-3">
            <span className="text-[10px] px-2 py-0.5 border border-[#eab308]/60 bg-[#eab308]/20 text-[#eab308] font-bold uppercase tracking-wider whitespace-nowrap">WHO ERF · Grade 3</span>
            <p className="text-xs md:text-sm text-foreground/90 leading-relaxed">
              The active Andes orthohantavirus (ANDV) cluster is being tracked under the WHO Emergency Response Framework at its highest internal severity — <strong className="text-[#eab308]">Grade 3</strong> — reflecting confirmed human-to-human transmission, elevated mortality, and the need for coordinated international surveillance, traveller screening and clinical preparedness in affected regions.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-4 md:px-6 py-6">
          {/* Biology & Transmission */}
          <div className="border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-hvi-primary text-lg">☣</span>
              <h2 className="text-lg font-semibold text-foreground">Biology &amp; Transmission</h2>
            </div>

            <div className="border border-border bg-background p-4">
              <span className="label-caps text-muted-foreground">Overview</span>
              <p className="text-sm text-hvi-on-surface-variant mt-2 leading-relaxed">
                Hantaviruses are a family of viruses spread mainly by rodents. They can cause diverse disease syndromes in people worldwide. Infection with any hantavirus can produce hantavirus disease in people.
              </p>
            </div>

            <div className="space-y-3 text-sm text-hvi-on-surface-variant">
              <div className="flex items-start gap-2">
                <span className="text-[#eab308]">⚠</span>
                <span><strong className="text-foreground">Primary Vector:</strong> Deer Mouse, Cotton Rat, Rice Rat, White-footed Mouse.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#f97316]">≋</span>
                <span><strong className="text-foreground">Transmission:</strong> Primarily through aerosolized rodent excreta (urine, feces, saliva).</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#ef4444]">✚</span>
                <span><strong className="text-foreground">Symptoms:</strong> Fatigue, fever, muscle aches, followed by coughing and shortness of breath.</span>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <span className="label-caps text-muted-foreground">Incubation Data</span>
              <div className="font-data text-2xl font-semibold text-hvi-primary mt-1">1-8 WEEKS</div>
            </div>
          </div>

          {/* Prevention Protocols — modernised */}
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-card via-card to-[#eab308]/5 p-5 space-y-4 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#eab308]/15 text-[#eab308]">🛡</span>
              <h2 className="text-lg font-semibold text-foreground">Prevention Protocols</h2>
            </div>

            <div className="rounded-xl border border-[#f97316]/40 bg-[#f97316]/10 px-4 py-3">
              <div className="flex items-start gap-2 text-sm">
                <span className="text-[#f97316] text-base leading-none">⛔</span>
                <span className="text-[#f97316] font-medium">Never sweep or vacuum rodent droppings — it aerosolises the virus.</span>
              </div>
            </div>

            <div className="space-y-3">
              <span className="label-caps text-muted-foreground block">Three core actions</span>

              {[
                { n: "1", icon: "🏠", title: "Seal entry points", body: "Close holes inside and outside the home with steel wool and caulk to block rodents.", color: "#3b82f6" },
                { n: "2", icon: "🪤", title: "Trap rodents safely", body: "Use snap traps in active areas. Wear gloves; spray traps with disinfectant before disposal.", color: "#eab308" },
                { n: "3", icon: "🧴", title: "Clean with the wet method", body: "Ventilate 30 min, wear N95/FFP2 + gloves, saturate with bleach 1:10, soak 5 min, wipe with paper towels, double-bag waste.", color: "#10b981", href: "#safe-cleaning", cta: "Read full WHO / CDC protocol" },
              ].map((step) => {
                const inner = (
                  <>
                    <div className="flex items-start gap-3">
                      <div
                        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold"
                        style={{ background: step.color + "20", color: step.color, border: `1px solid ${step.color}55` }}
                      >
                        {step.n}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span aria-hidden>{step.icon}</span>
                          <span className="font-semibold text-foreground text-sm">{step.title}</span>
                        </div>
                        <p className="text-sm text-hvi-on-surface-variant mt-1 leading-relaxed">{step.body}</p>
                        {step.cta && (
                          <span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-hvi-primary">
                            {step.cta} <span aria-hidden>→</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                );
                return step.href ? (
                  <a
                    key={step.n}
                    href={step.href}
                    className="block rounded-xl border border-border bg-background/60 p-4 transition hover:border-hvi-primary hover:bg-background hover:shadow-sm"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={step.n} className="rounded-xl border border-border bg-background/60 p-4">
                    {inner}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Clinical Guidelines */}
          <div className="border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-[#3b82f6] text-lg">📋</span>
              <h2 className="text-lg font-semibold text-foreground">Clinical Guidelines</h2>
            </div>
            <p className="text-xs text-muted-foreground">
              Official clinical and public-health references. Links open the publishing institution's website.
            </p>

            <div className="space-y-0">
              {[
                {
                  title: "WHO — Hantavirus disease",
                  sub: "World Health Organization · health topic overview",
                  href: "https://www.who.int/health-topics/hantavirus",
                },
                {
                  title: "CDC — Clinical Overview of Hantavirus",
                  sub: "U.S. Centers for Disease Control and Prevention · diagnosis & management",
                  href: "https://www.cdc.gov/hantavirus/hcp/clinical-overview/index.html",
                },
                {
                  title: "CDC — Hantavirus Pulmonary Syndrome (HPS)",
                  sub: "Surveillance case definition and laboratory testing",
                  href: "https://www.cdc.gov/hantavirus/hps/index.html",
                },
                {
                  title: "ECDC — Hantavirus infection factsheet",
                  sub: "European Centre for Disease Prevention and Control",
                  href: "https://www.ecdc.europa.eu/en/hantavirus-infection/facts",
                },
                {
                  title: "PAHO — Hantavirus in the Americas",
                  sub: "Pan American Health Organization · regional guidance",
                  href: "https://www.paho.org/en/topics/hantavirus",
                },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between py-3 border-b border-border last:border-b-0 hover:bg-background/50 transition group"
                >
                  <div>
                    <div className="text-sm font-semibold text-foreground group-hover:text-hvi-primary transition">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.sub}</div>
                  </div>
                  <span className="text-muted-foreground group-hover:text-hvi-primary transition">↗</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Safe Cleaning — long-form WHO/CDC protocol */}
        <article id="safe-cleaning" className="px-4 md:px-6 pb-2 scroll-mt-20">
          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-hvi-primary text-lg">🧪</span>
              <h2 className="text-xl font-bold text-foreground">
                Safe Cleaning of Rodent-Contaminated Areas: The Wet Method
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-5">
              Evidence-based protocol synthesised from WHO Hantavirus guidance, the U.S. CDC{" "}
              <em>Cleaning Up After Rodents</em> framework, ECDC factsheets and the U.S. EPA disinfectant list.
              Last reviewed {new Date().toISOString().slice(0, 10)}.
            </p>

            {/* Quick checklist accordion */}
            <div className="mb-6 rounded-2xl border border-border bg-gradient-to-br from-card to-hvi-primary/5 p-4 md:p-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-hvi-primary/15 text-hvi-primary text-sm">✓</span>
                <h3 className="text-base font-semibold text-foreground">One-page Quick Checklist</h3>
                <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground hidden sm:inline">Tap to expand</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">A condensed reference. Always read the full sections below before performing cleanup.</p>

              <div className="space-y-2">
                {[
                  {
                    icon: "🌬",
                    color: "#3b82f6",
                    title: "Ventilation",
                    summary: "Open doors/windows for 30 min before entering.",
                    items: [
                      "Open all doors and windows for at least 30 minutes.",
                      "Leave the building during ventilation; use cross-draught.",
                      "Do NOT use fans, leaf-blowers or forced-air systems.",
                      "High-risk persons (pregnant, immunocompromised, children) should not perform cleanup.",
                    ],
                  },
                  {
                    icon: "🥽",
                    color: "#eab308",
                    title: "PPE",
                    summary: "Gloves + N95 / FFP2 minimum. P100 / FFP3 for heavy contamination.",
                    items: [
                      "Disposable nitrile, latex, vinyl or rubber gloves.",
                      "N95 / FFP2 respirator (fit-tested) — not a surgical mask.",
                      "Heavy contamination: P100 / FFP3 respirator, coveralls, boots, eyewear, double gloves.",
                      "Closed shoes; clothing washable in hot water.",
                    ],
                  },
                  {
                    icon: "🧴",
                    color: "#10b981",
                    title: "Wet method — 6 steps",
                    summary: "Saturate → soak 5 min → lift → re-disinfect → mop → launder.",
                    items: [
                      "Bleach 1:10 (1 part bleach, 9 parts cold water), mixed fresh same-day.",
                      "Saturate droppings & nests with a low-pressure squirt bottle (no atomising spray).",
                      "Soak ≥ 5 minutes (10 min for heavy contamination).",
                      "Lift with paper towels — never sweep, never vacuum.",
                      "Wipe surface a second time with disinfectant; air-dry.",
                      "Mop floors; steam-clean upholstery; wash fabrics at 60 °C.",
                    ],
                  },
                  {
                    icon: "🗑",
                    color: "#f97316",
                    title: "Waste disposal",
                    summary: "Double-bag everything. Never burn or bury.",
                    items: [
                      "Spray dead rodents and traps with disinfectant; soak 5 min.",
                      "Place rodent + trap into a sealed plastic bag.",
                      "Double-bag and dispose per local regulations (household waste or biohazard).",
                      "Never handle rodents with bare hands. Never burn or bury contaminated material.",
                    ],
                  },
                  {
                    icon: "🏥",
                    color: "#ef4444",
                    title: "Post-exposure",
                    summary: "Watch for symptoms 1–8 weeks. Seek care urgently.",
                    items: [
                      "Watch for fever, severe muscle aches, headache, fatigue, GI symptoms.",
                      "Later: cough and shortness of breath = medical emergency.",
                      "Window: 1 to 8 weeks after exposure.",
                      "Contact a clinician immediately and disclose the rodent exposure.",
                    ],
                  },
                ].map((c, i) => (
                  <details
                    key={c.title}
                    open={i === 0}
                    className="group rounded-xl border border-border bg-background/70 open:bg-background open:shadow-sm transition"
                  >
                    <summary
                      className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 select-none"
                      style={{ borderLeft: `3px solid ${c.color}` }}
                    >
                      <span
                        className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm"
                        style={{ background: c.color + "20", color: c.color }}
                        aria-hidden
                      >
                        {c.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-foreground text-sm">{c.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.summary}</div>
                      </div>
                      <span className="text-muted-foreground transition-transform group-open:rotate-180" aria-hidden>⌄</span>
                    </summary>
                    <ul className="px-4 pb-4 pt-1 pl-14 list-disc space-y-1 text-sm text-hvi-on-surface-variant marker:text-muted-foreground">
                      {c.items.map((it) => (
                        <li key={it}>{it}</li>
                      ))}
                    </ul>
                  </details>
                ))}
              </div>
            </div>
            <div className="prose-hvi space-y-6 text-sm text-hvi-on-surface-variant leading-relaxed max-w-none">
              <section>
                <p>
                  Hantaviruses are transmitted to humans almost exclusively through{" "}
                  <strong className="text-foreground">aerosolised rodent excreta</strong> — urine, droppings,
                  saliva and nesting material that become airborne when disturbed. The single most important
                  rule of household and occupational decontamination is therefore deceptively simple:
                  <strong className="text-foreground"> never generate dust.</strong> Sweeping with a dry broom,
                  shaking out blankets, beating mattresses or running a household vacuum cleaner over a
                  contaminated surface all release virus-laden particles into the breathing zone of the person
                  doing the cleaning. The international consensus — codified by the WHO, the U.S. CDC and the
                  European Centre for Disease Prevention and Control (ECDC) — is that contaminated areas must
                  be cleaned exclusively by a <strong className="text-foreground">wet method</strong>: surfaces
                  are saturated with disinfectant <em>before</em> they are touched, allowed to soak, and then
                  wiped up with disposable absorbent material that is sealed and discarded.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">1. Before you enter the space</h3>
                <p>
                  Closed cabins, sheds, basements, attics, barns and storage rooms that have been undisturbed
                  for weeks or months carry the highest risk. WHO and CDC both recommend{" "}
                  <strong className="text-foreground">opening all doors and windows for at least 30 minutes
                  before entering</strong> and then leaving the building during ventilation. Use cross-draught
                  if possible. Do not use forced-air systems, fans or leaf-blowers, which re-aerosolise
                  settled dust. People with chronic respiratory conditions, immunocompromised individuals,
                  pregnant people and children should not perform the cleaning.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  2. Personal protective equipment (PPE)
                </h3>
                <p>
                  For light contamination in well-ventilated indoor areas, the minimum PPE recommended by CDC
                  and ECDC is:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Disposable rubber, latex, vinyl or nitrile <strong className="text-foreground">gloves</strong>.</li>
                  <li>An <strong className="text-foreground">N95 / FFP2 respirator</strong> (a fit-tested device, not a surgical mask) when handling visible droppings, urine, nests, dead rodents or working in enclosed spaces.</li>
                  <li>Closed shoes and clothing that can be washed in hot water immediately afterwards.</li>
                </ul>
                <p>
                  For heavy infestations, structures closed for long periods, or any setting with active
                  rodent activity, WHO advises full PPE: a <strong className="text-foreground">P100/FFP3
                  respirator with HEPA-equivalent filter</strong>, disposable coveralls, rubber boots,
                  protective eyewear and a second pair of gloves. Heavy contamination should normally be
                  handled by a professional pest-control or environmental-services operator following local
                  occupational-health rules.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  3. Preparing the disinfectant
                </h3>
                <p>
                  The reference disinfectant in WHO and CDC documents is{" "}
                  <strong className="text-foreground">freshly prepared sodium hypochlorite (household
                  bleach)</strong> at a 1:10 dilution — one part household bleach (typically 5–6% sodium
                  hypochlorite) to nine parts cold water. The solution loses activity within roughly 24 hours
                  and must be mixed on the day of use, in a labelled container, never combined with ammonia
                  or other cleaning chemicals.
                </p>
                <p>
                  Where bleach is not appropriate (e.g. delicate surfaces, food-contact areas, sensitive
                  electronics nearby), use a commercial disinfectant explicitly registered against
                  enveloped viruses by your national authority — for example a product on the U.S. EPA{" "}
                  <em>List L: Disinfectants Effective Against Emerging Viral Pathogens</em>, or a
                  national equivalent. Follow the contact-time on the product label; for most quaternary-
                  ammonium and accelerated-hydrogen-peroxide formulations this is between 1 and 10 minutes.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  4. Step-by-step wet-cleanup procedure
                </h3>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>
                    <strong className="text-foreground">Saturate, do not spray-mist.</strong> Apply the
                    disinfectant directly onto the droppings, urine stains, nests and surrounding surface
                    using a low-pressure squirt bottle until the material is visibly wet. Do not use an
                    atomising spray that produces a fine aerosol.
                  </li>
                  <li>
                    <strong className="text-foreground">Soak for at least 5 minutes</strong> (10 minutes for
                    heavier contamination, or as instructed on the disinfectant label). This is the contact
                    time required to inactivate the virus and to bind the material so it cannot become
                    airborne when handled.
                  </li>
                  <li>
                    <strong className="text-foreground">Pick up — do not sweep.</strong> Use a paper towel or
                    disposable cloth to lift the soaked material off the surface. Place it directly into a
                    plastic rubbish bag — do not shake, transfer or compress it.
                  </li>
                  <li>
                    <strong className="text-foreground">Disinfect the surface a second time.</strong> Wipe the
                    cleaned area with a fresh paper towel saturated with the same disinfectant. Allow it to
                    air-dry; do not rinse.
                  </li>
                  <li>
                    <strong className="text-foreground">Mop hard floors</strong> with the disinfectant
                    solution. Disinfect counters, cabinets, drawers and any surface that may have come into
                    contact with rodents or their excreta.
                  </li>
                  <li>
                    <strong className="text-foreground">Soft furnishings.</strong> Steam-clean or shampoo
                    upholstered furniture and carpets. Machine-wash bedding, curtains and clothing in hot
                    water (60 °C / 140 °F) with detergent and dry on a hot cycle or in direct sunlight.
                    Items that cannot be washed should be discarded.
                  </li>
                </ol>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  5. Dead rodents, traps and nesting material
                </h3>
                <p>
                  Spray the carcass and the trap with disinfectant until thoroughly wet, leave for 5 minutes,
                  then place the entire trap and rodent into a sealed plastic bag. Place that bag inside a
                  second bag (double-bagging) and dispose of it according to local regulations — generally
                  with household waste in countries where no specific rule applies, or via biohazard waste
                  channels in occupational settings. Never handle a rodent with bare hands and never burn or
                  bury contaminated material in a way that creates dust or smoke exposure.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  6. Removing PPE and personal hygiene
                </h3>
                <p>
                  Before leaving the cleaning area, wash your gloved hands with soap and water, then with the
                  disinfectant. Remove the respirator last, holding it by the straps and avoiding contact
                  with the front. Place gloves and respirator in the waste bag, seal it, and wash your bare
                  hands and forearms thoroughly with soap and warm water for at least 20 seconds. Launder
                  the clothing worn during cleanup separately on a hot cycle. Shower as soon as practical.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  7. When to call a professional
                </h3>
                <p>
                  WHO and national public-health agencies recommend professional remediation for: structures
                  with active or extensive rodent infestation; spaces closed for more than three months;
                  enclosed crawl-spaces and ventilation systems; settings where occupational exposure is
                  recurrent (forestry, grain storage, military, field research). In an outbreak setting,
                  follow the instructions of the responding public-health authority — the cleanup of a
                  documented hantavirus exposure site is a regulated activity in most jurisdictions.
                </p>
              </section>

              <section>
                <h3 className="text-base font-semibold text-foreground mt-2">
                  8. Post-exposure: when to seek medical care
                </h3>
                <p>
                  If you develop fever, severe muscle aches, headache, fatigue, gastrointestinal symptoms or,
                  later, cough and shortness of breath within <strong className="text-foreground">1 to 8
                  weeks</strong> after a possible exposure to rodent excreta, contact a clinician
                  immediately and disclose the exposure. Hantavirus pulmonary syndrome (HPS/HCPS) deteriorates
                  rapidly; early access to intensive care, including extracorporeal membrane oxygenation
                  (ECMO) where available, is the principal determinant of survival.
                </p>
              </section>

              <section className="border-t border-border pt-4">
                <h3 className="text-base font-semibold text-foreground">References</h3>
                <ul className="list-disc pl-6 space-y-1 text-xs text-muted-foreground">
                  <li>
                    World Health Organization — <em>Hantavirus disease</em> health topic.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.who.int/health-topics/hantavirus" target="_blank" rel="noopener noreferrer">who.int/health-topics/hantavirus</a>
                  </li>
                  <li>
                    World Health Organization, Regional Office for the Americas (PAHO) —{" "}
                    <em>Hantavirus</em> regional guidance.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.paho.org/en/topics/hantavirus" target="_blank" rel="noopener noreferrer">paho.org/en/topics/hantavirus</a>
                  </li>
                  <li>
                    U.S. Centers for Disease Control and Prevention — <em>Cleaning Up After Rodents</em>.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.cdc.gov/rodents/cleaning/index.html" target="_blank" rel="noopener noreferrer">cdc.gov/rodents/cleaning</a>
                  </li>
                  <li>
                    U.S. CDC — <em>Hantavirus Pulmonary Syndrome (HPS): Prevention</em>.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.cdc.gov/hantavirus/prevention/index.html" target="_blank" rel="noopener noreferrer">cdc.gov/hantavirus/prevention</a>
                  </li>
                  <li>
                    European Centre for Disease Prevention and Control — <em>Hantavirus infection</em> factsheet.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.ecdc.europa.eu/en/hantavirus-infection/facts" target="_blank" rel="noopener noreferrer">ecdc.europa.eu/en/hantavirus-infection/facts</a>
                  </li>
                  <li>
                    U.S. Environmental Protection Agency — <em>List L: Disinfectants for Emerging Viral Pathogens</em>.{" "}
                    <a className="text-hvi-primary hover:underline" href="https://www.epa.gov/pesticide-registration/list-l-disinfectants-emerging-viral-pathogens-evps-list" target="_blank" rel="noopener noreferrer">epa.gov/…/list-l</a>
                  </li>
                </ul>
                <p className="text-[11px] text-muted-foreground mt-3">
                  This article is a public-information summary and does not replace local occupational-health
                  regulations or the directives of a competent public-health authority during an outbreak.
                </p>
              </section>
            </div>
          </div>
        </article>

        {/* WHO Emergency Response Framework */}
        <div className="px-4 md:px-6 pb-2">
          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[#eab308] text-lg">⚠</span>
              <h2 className="text-xl font-bold text-foreground">WHO Emergency Response Framework</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              The World Health Organization classifies acute public-health events using its Emergency Response Framework (ERF). It defines three internal severity grades plus the formal PHEIC declaration — there is no &quot;Level 4&quot;. The active Andes orthohantavirus cluster is currently tracked at <strong className="text-[#eab308]">Grade 3</strong>.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  level: "Grade 1",
                  name: "Limited event",
                  color: "#3b82f6",
                  desc: "Single-country event with minimal public-health consequences. Country office leads response with light WHO support.",
                },
                {
                  level: "Grade 2",
                  name: "Moderate event",
                  color: "#eab308",
                  desc: "Single or multi-country event with moderate consequences requiring a moderate WHO response across country and regional levels.",
                },
                {
                  level: "Grade 3",
                  name: "Major / complex event",
                  color: "#f97316",
                  desc: "Single or multi-country event with substantial public-health consequences requiring a substantial WHO response across all three levels of the organization.",
                  current: true,
                },
                {
                  level: "PHEIC",
                  name: "Public Health Emergency of International Concern",
                  color: "#ef4444",
                  desc: "Formal declaration by the WHO Director-General under the International Health Regulations (2005) when an event constitutes a risk to other states and may require a coordinated international response.",
                },
              ].map((a) => (
                <div
                  key={a.level}
                  className="border border-border bg-card p-4"
                  style={{ borderLeft: `4px solid ${a.color}` }}
                >
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="font-data text-[10px] px-2 py-0.5 font-bold uppercase tracking-wider"
                        style={{ background: a.color + "20", color: a.color, border: `1px solid ${a.color}80` }}
                      >
                        {a.level}
                      </span>
                      <span className="font-semibold text-foreground text-sm">{a.name}</span>
                    </div>
                    {a.current && (
                      <span className="text-[10px] px-2 py-0.5 bg-[#f97316]/15 text-[#f97316] font-bold uppercase tracking-wider flex items-center gap-1 flex-shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#f97316] animate-pulse" />
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-hvi-on-surface-variant leading-relaxed">{a.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground mt-3 uppercase tracking-wider">
              Source: WHO Emergency Response Framework (2nd ed.) · IHR (2005)
            </p>
          </div>
        </div>

        {/* Hantavirus Variants Reference */}
        <div className="px-4 md:px-6 pb-8">
          <div className="border-t border-border pt-6">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-hvi-primary text-lg">🧬</span>
              <h2 className="text-xl font-bold text-foreground">Hantavirus Variant Reference</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-5">Known pathogenic hantavirus species, their reservoir hosts, geographic distribution, and associated mortality rates.</p>

            {/* Disease type legend */}
            <div className="flex flex-wrap gap-3 mb-4 text-[10px] uppercase tracking-wider">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#ef4444]" />
                <span className="text-muted-foreground">HPS — Hantavirus Pulmonary Syndrome</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#f97316]" />
                <span className="text-muted-foreground">HFRS — Hemorrhagic Fever with Renal Syndrome</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#3b82f6]" />
                <span className="text-muted-foreground">NE — Nephropathia Epidemica</span>
              </span>
            </div>

            <div className="space-y-3">
              {VARIANTS.map((v) => {
                const diseaseColor = v.disease.includes("HPS") ? "#ef4444" : v.disease.includes("HFRS") ? "#f97316" : v.disease.includes("NE") ? "#3b82f6" : "#a78b7d";
                return (
                  <div
                    key={v.abbreviation}
                    className={`border bg-card p-4 transition ${v.active ? "border-[#ef4444]/50 bg-[#ef4444]/5" : "border-border"}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground">{v.name}</span>
                          <span className="font-data text-xs px-2 py-0.5 border border-border text-hvi-primary">{v.abbreviation}</span>
                          {v.active && (
                            <span className="text-[10px] px-2 py-0.5 bg-[#ef4444]/15 text-[#ef4444] font-bold uppercase tracking-wider flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] animate-pulse" />
                              Active Outbreak
                            </span>
                          )}
                        </div>
                        <span className="text-xs mt-1 inline-block px-2 py-0.5" style={{ background: diseaseColor + "20", color: diseaseColor }}>
                          {v.disease}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="label-caps text-muted-foreground">Mortality</div>
                        <div className="font-data text-lg font-bold text-foreground">{v.mortality}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 text-xs">
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground flex-shrink-0">🌍</span>
                        <div>
                          <span className="label-caps text-muted-foreground block mb-0.5">Region</span>
                          <span className="text-hvi-on-surface-variant">{v.region}</span>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-muted-foreground flex-shrink-0">🐀</span>
                        <div>
                          <span className="label-caps text-muted-foreground block mb-0.5">Reservoir Host</span>
                          <span className="text-hvi-on-surface-variant italic">{v.reservoir}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>



        <FeedbackForm />

        <div className="px-4 md:px-6 pb-8">
          <AdSlot slot={import.meta.env.VITE_ADSENSE_SLOT_RESOURCES as string | undefined} label="Sponsored" />
        </div>
      </main>

      <HVIFooter />
    </div>
  );
}
